import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import API_ENDPOINTS from "@/common/api/apiEndpoints";
import httpClient from "@/common/api/httpClient";
import httpMethods from "@/common/api/httpMethods";
import type { DefectLog, Paginated } from "@/common/types/generated";
import { TableConfig } from "@/config/appConfig";
interface QueryParams {
  versionId: number;

  page?: number;
  size?: number;
  query?: string;
  sort?: string;
}

const buildQueryParams = (params: QueryParams): string => {
  const {
    versionId,

    page = 0,
    size = TableConfig.ITEMS_PER_PAGE,
    query = "",
    sort = "name,asc",
  } = params;

  const searchParams = new URLSearchParams({
    versionId: String(versionId),

    //testCaseFolderId: String(testCaseFolderId),
    page: String(page),
    size: String(size),
    query,
    sort,
  });

  return `?${searchParams.toString()}`;
};

type GenericQueryParams = {
  query?: string;
  sort?: string;
  page?: number;
  size?: number;
  [key: string]: string | number | boolean | null | undefined;
};

export const buildSpecialQueryParams = (params: GenericQueryParams): string => {
  const { query, ...rest } = params;

  const searchParams = new URLSearchParams();

  Object.entries(rest).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      searchParams.set(key, String(value));
    }
  });

  // Manually prepend `query` if present
  const queryPart = query ? `query=${query}` : "";

  return `?${[queryPart, searchParams.toString()].filter(Boolean).join("&")}`;
};

export const fetchDefectLogs = (page: number, versionId: number) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}?query=deleted:false,versionId:${versionId}&sort=title,asc&page=${page}&size=${TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<DefectLog>>(httpMethods.GET, endpoint);
};

export const fetchAllDefects = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}${buildQueryParams(params)}`;
  return httpClient<Paginated<DefectLog>>(httpMethods.GET, endpoint);
};

export const fetchAllProjectDefects = (page: number = 0) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}?query=deleted:false&sort=title,asc&page=${page}&size=${TableConfig.ITEMS_PER_PAGE}`;
  return httpClient<Paginated<DefectLog>>(httpMethods.GET, endpoint);
};

export const fetchAssignedDefects = (params: QueryParams) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.assigneddefects}${buildQueryParams(params)}`;
  return httpClient<Paginated<DefectLog>>(httpMethods.GET, endpoint);
};

export const fetchDefectById = async (id: number | string) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}/${id}`;
  const response = await httpClient<DefectLog>(httpMethods.GET, endpoint);
  return response.data; // unwrap to return the actual defect
};

export const exportDefect = async (
  params: QueryParams,
  typeFormat: string,
  mode: "all" | "assigned",
) => {
  let response: any = { data: { content: [] } };
  if (mode === "all") {
    const endpoint = `${API_ENDPOINTS.DEFECTLOG.export}/${typeFormat}`;
    response = await httpClient<Paginated<DefectLog>>(
      httpMethods.GET,
      endpoint,
    );
  }
  if (mode === "assigned") {
    const endpoint = `${API_ENDPOINTS.DEFECTLOG.assigneddefects}${buildQueryParams(params)}`;
    response = await httpClient<Paginated<DefectLog>>(
      httpMethods.GET,
      endpoint,
    );
  }
  const defects = response.data.content;

  // ✅ Prepare filtered data
  const filteredDefects = defects.map((d: any) => ({
    id: d.id,
    testcaseId: d.testcaseId,
    stepAction: d.stepAction,
    testCaseName: d.testCaseName,
    defectStatus: d.defectStatus,
    priority: d.priority,
    date: d.createdDate ? new Date(d.createdDate).toLocaleString("en-GB") : "",
  }));

  const header = [
    "id",
    "testcaseId",
    "stepAction",
    "testCaseName",
    "defectStatus",
    "priority",
    "date",
  ];

  if (typeFormat === "excel") {
    const worksheet = XLSX.utils.aoa_to_sheet([["Defect Report"]]);

    // ✅ Merge title row across all columns
    const mergeRef = { s: { r: 0, c: 0 }, e: { r: 0, c: header.length - 1 } };
    worksheet["!merges"] = worksheet["!merges"] || [];
    worksheet["!merges"].push(mergeRef);

    // ✅ Add table data starting at A2
    XLSX.utils.sheet_add_json(worksheet, filteredDefects, {
      header,
      origin: "A2", // This works correctly with sheet_add_json
    });

    // ✅ Auto column width
    const colWidths = header.map((h) => ({
      wch:
        Math.max(
          h.length,
          ...filteredDefects.map((row: any) =>
            row[h] ? row[h].toString().length : 0,
          ),
        ) + 2,
    }));
    worksheet["!cols"] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Defects");

    XLSX.writeFile(workbook, "defects.xlsx");
  } else if (typeFormat === "pdf") {
    // ---------- PDF ----------
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Defect Report", 14, 15);

    autoTable(doc, {
      startY: 25,
      head: [header],
      body: filteredDefects.map((row: any) => header.map((h) => row[h] ?? "")),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: {
        fillColor: [41, 128, 185],
        textColor: 255,
        fontStyle: "bold",
      },
    });

    doc.save("defects.pdf");
  } else {
    console.warn("Unsupported typeFormat:", typeFormat);
  }
};

export const createDefectLog = (
  payload: Partial<DefectLog>,
  _files?: File[],
) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}`;
  return httpClient<void>(httpMethods.POST, endpoint, payload);
};

export const updateDefectLog = (
  defectId: string | number,
  payload: Partial<DefectLog>,
  _files?: File[],
) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}/${defectId}`;
  return httpClient<void>(httpMethods.PUT, endpoint, payload);
};

export const deleteDefectLog = (defectId: string | number) => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}/${defectId}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

export const bulkDeleteDefectLogs = (ids: (string | number)[]) => {
  const idsParam = ids.map((id) => `ids[]=${id}`).join("&");
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.defectLog}/bulk?${idsParam}`;
  return httpClient<void>(httpMethods.DELETE, endpoint);
};

/* ------------------------------------------------------------------ */
/*  Linked Test Cases                                                  */
/* ------------------------------------------------------------------ */

export type LinkedTestCaseItem = {
  id?: number;
  defectId?: number;
  testCaseId: number;
  testCaseName?: string;
  testCaseType?: string;
  testStepId?: number | null;
  testStepName?: string;
  createdDate?: string;
};

export const fetchLinkedTestCases = async (
  defectId: number,
): Promise<LinkedTestCaseItem[]> => {
  const endpoint = API_ENDPOINTS.DEFECTLOG.linkedTestCases(defectId);
  const response = await httpClient<LinkedTestCaseItem[]>(
    httpMethods.GET,
    endpoint,
  );
  return response.data;
};

export const linkTestCases = async (
  defectId: number,
  items: LinkedTestCaseItem[],
): Promise<LinkedTestCaseItem[]> => {
  const endpoint = API_ENDPOINTS.DEFECTLOG.linkedTestCases(defectId);
  const response = await httpClient<LinkedTestCaseItem[]>(
    httpMethods.POST,
    endpoint,
    items,
  );
  return response.data;
};

export const unlinkTestCase = async (
  defectId: number,
  linkId: number,
): Promise<void> => {
  const endpoint = `${API_ENDPOINTS.DEFECTLOG.linkedTestCases(defectId)}/${linkId}`;
  await httpClient<void>(httpMethods.DELETE, endpoint);
};

export type DefectRelationshipType =
  | "RELATES_TO"
  | "BLOCKED_BY"
  | "DUPLICATE_OF";

export type DefectReferenceItem = {
  id: number;
  sourceDefectId: number;
  targetDefectId: number;
  relationshipType: DefectRelationshipType;
  createdDate?: string;
  targetDefect?: {
    id: number;
    title?: string | null;
    defectStatus?: string | null;
    severity?: string | null;
    priority?: string | null;
    defectKey?: string | null;
  };
};

export type DefectReferenceUpsert = {
  targetDefectId: number;
  relationshipType: DefectRelationshipType;
};

export const fetchDefectReferences = async (
  defectId: number,
): Promise<DefectReferenceItem[]> => {
  const endpoint = API_ENDPOINTS.DEFECTLOG.references(defectId);
  const response = await httpClient<DefectReferenceItem[]>(
    httpMethods.GET,
    endpoint,
  );
  return response.data;
};

export const replaceDefectReferences = async (
  defectId: number,
  references: DefectReferenceUpsert[],
): Promise<DefectReferenceItem[]> => {
  const endpoint = API_ENDPOINTS.DEFECTLOG.references(defectId);
  const response = await httpClient<DefectReferenceItem[]>(
    httpMethods.PUT,
    endpoint,
    {
      references,
    },
  );
  return response.data;
};

export type DefectFolderTreeData = {
  unlinked: number;
  folders: Array<{
    id: number;
    name: string;
    cycles: Array<{ id: number; name: string; count: number }>;
  }>;
};

export const fetchDefectFolderTree = (versionId: number) =>
  httpClient<DefectFolderTreeData>(
    httpMethods.GET,
    `${API_ENDPOINTS.DEFECTLOG.folderTree}?versionId=${versionId}`,
  );
